import { clsx } from 'clsx';
import { forwardRef } from 'react';

import { INPUT } from 'UI/Forms/Fields/constants';
import { LABEL_COLOR_CLS, LABEL_TYPOGRAPHY_CLS } from 'UI/Forms/constants';
import { buttonStyles } from 'UI/Forms/Button/styles';

import type { ButtonT } from 'UI/Forms/Button/types';
import type { ClassNameT } from 'UI/types';
import type { LabelFieldsT, SizeT } from '../types';

type InputPropsT = LabelFieldsT & {
  readonly size: SizeT;
  readonly rootClassName?: ClassNameT;
  readonly wide?: boolean;
  readonly ariaLabel?: string;
  readonly dataTestId?: string;
  readonly value?: string;
  readonly buttonVariant?: ButtonT['variant'];
  readonly buttonText?: string;
  readonly onButtonClick?: (
    event: React.SyntheticEvent<HTMLButtonElement>,
  ) => void;
  readonly label?: React.ReactNode;
  readonly disabled?: boolean;
};

export const InputWithActionButton = forwardRef<HTMLInputElement, InputPropsT>(
  (
    {
      wide,
      ariaLabel,
      rootClassName,
      dataTestId,
      disabled,
      value,
      label,
      size = 'medium',
      buttonVariant = 'black',
      buttonText = 'Copy',
      onButtonClick,
      labelColor = 'text-2',
      labelTypography = 'large-r',
      ...inputRestProps
    },
    ref,
  ) => {
    const { rootCls: buttonStyleVariant } = buttonStyles({
      variant: buttonVariant,
    });
    return (
      <label className={clsx(wide && 'w-full', rootClassName)}>
        {label ? (
          <span
            className={clsx(
              'mb-8 inline-block',
              LABEL_COLOR_CLS[labelColor],
              LABEL_TYPOGRAPHY_CLS[labelTypography],
            )}
          >
            {label}
          </span>
        ) : null}

        <div className={clsx('flex', wide && 'w-full')}>
          <input
            type="text"
            {...inputRestProps}
            aria-label={ariaLabel}
            className={clsx(
              'inline-block text-ellipsis bg-clip-padding',
              INPUT.size[size].input.height,
              INPUT.size[size].input.radius,
              INPUT.size[size].input.padding,
              INPUT.size[size].input.typography,
              INPUT.state.disabled,
              wide && 'w-full',
              'vendor-clear-input-button rounded-r-0 border-r-0 border-black-10 bg-black-5 read-only:cursor-default read-only:opacity-100 dark:border-black-30 dark:bg-black-30/20 dark:text-black-10',
            )}
            data-test-id={dataTestId}
            disabled={disabled}
            ref={ref}
            value={value}
          />

          <button
            className={clsx(
              buttonStyleVariant(),
              INPUT.size[size].input.height,
              INPUT.size[size].input.radius,
              'w-80 rounded-l-0 px-18 text-medium-b md:w-100',
            )}
            data-test-id={`${buttonText}-button`}
            disabled={disabled}
            type="button"
            onClick={onButtonClick}
          >
            {buttonText}
          </button>
        </div>
      </label>
    );
  },
);
